"use client";

import Link from "next/link";
import Image from "next/image";
import { GenreEvent } from "@types";
import { CalendarIcon } from "@heroicons/react/20/solid";
import { MapPinIcon, MegaphoneIcon } from "@heroicons/react/24/solid";
import { useResizeObserver, useWindowSize } from "@utils/hooks";
import { motion } from "framer-motion";
import { useState } from "react";
import { joinClassNames } from "@utils/helpers";
import { useIsShopsite } from "@context/DomainContext";
import { Badge, Text } from "@components/theming";

interface Props {
  /**
   * The event associated to the current genre selected
   */
  event: GenreEvent;
  imageIsPriority?: boolean;
}

/**
 * This component will be used in a listing component to list all events based on a genre
 */
export default function GenreEventLink({
  event,
  imageIsPriority = false,
}: Props) {
  const [hoveringDate, setHoveringDate] = useState(false);
  const [dateRangeVisible, setDateRangeVisible] = useState(false);
  const windowSize = useWindowSize();
  const isShopsite = useIsShopsite();
  const { ref, clientHeight } = useResizeObserver();

  const appropriate_image =
    windowSize.width && windowSize.width < 768
      ? event.images.find((image) => image.format.name.includes("Banner"))
      : event.images.find((image) => image.format.name.includes("Portrait"));
  const imageSrc = appropriate_image
    ? `${appropriate_image.path.processed.trim()}?v=${
        appropriate_image.version
      }`
    : undefined;

  return (
    <Link
      href={event.slug || "#"}
      onMouseEnter={(e) => {
        e.preventDefault();
        setHoveringDate(true);
        setDateRangeVisible(true);
      }}
      onMouseLeave={(e) => {
        e.preventDefault();
        setHoveringDate(false);
        setTimeout(() => setDateRangeVisible(false), 200);
      }}
      className={joinClassNames(
        "relative group flex flex-col gap-1 p-2 w-full h-fit transition-colors ease-in-out duration-500",
        isShopsite
          ? "sm:hover:bg-indigo-50/75 rounded-2xl"
          : "sm:hover:bg-[color:var(--accent-a2)] rounded-[var(--radius-4)]"
      )}
    >
      <div className="relative">
        {imageSrc && windowSize.width && windowSize.width < 768 ? (
          <Image
            src={imageSrc}
            alt={event.name}
            priority={imageIsPriority}
            width={192}
            height={96}
            className={joinClassNames(
              "object-cover w-full aspect-[2/1]",
              isShopsite ? "rounded-xl" : "rounded-[var(--radius-3)]"
            )}
          />
        ) : imageSrc && windowSize.width && windowSize.width >= 768 ? (
          <Image
            src={imageSrc}
            alt={event.name}
            priority={imageIsPriority}
            width={192}
            height={224}
            className={joinClassNames(
              "object-cover w-full aspect-[6/7]",
              isShopsite ? "rounded-xl" : "rounded-[var(--radius-3)]"
            )}
          />
        ) : (
          <div
            className={joinClassNames(
              "flex items-center justify-center w-full aspect-[2/1] md:aspect-[6/7]",
              isShopsite
                ? "bg-indigo-50 rounded-xl"
                : "bg-[color:var(--accent-a2)] rounded-[var(--radius-3)]"
            )}
          >
            <MegaphoneIcon
              className={joinClassNames(
                "w-1/2 h-1/2",
                isShopsite ? "text-indigo-800" : "text-[color:var(--accent-9)]"
              )}
            />
          </div>
        )}
        {event.daterange ? (
          <>
            {/* invisible span to measure height of the text */}
            <span
              className="invisible text-sm max-w-[calc(100%-48px)] absolute"
              ref={ref}
            >
              {event.daterange}
            </span>
            <div
              className={joinClassNames(
                "hidden sm:flex items-center absolute bottom-2 right-2 ml-2 p-1 text-sm overflow-clip",
                dateRangeVisible ? "gap-1" : "",
                isShopsite
                  ? "bg-indigo-100 text-indigo-800 rounded-lg"
                  : "bg-[color:var(--accent-3)] text-[color:var(--accent-a11)] backdrop-blur rounded-[var(--radius-2)]"
              )}
            >
              <div className="flex items-center justify-center h-5 w-5 shrink-0">
                <CalendarIcon className="h-4 w-4 shrink-0" />
              </div>
              <motion.span
                initial={{
                  width: 0,
                  height: "20px",
                  opacity: 0,
                }}
                animate={
                  hoveringDate
                    ? {
                        width: "auto",
                        height: clientHeight,
                        opacity: 1,
                        transition: { opacity: { delay: 0.3 } },
                      }
                    : {
                        width: 0,
                        height: "20px",
                        opacity: 0,
                        transition: {
                          width: { delay: 0.2 },
                          height: { delay: 0.2 },
                        },
                      }
                }
                className="pointer-events-none will-change-transform"
              >
                {event.daterange}
              </motion.span>
            </div>
          </>
        ) : (
          <></>
        )}
      </div>
      <Text
        as="p"
        size="4"
        weight="bold"
        whitelabelClasses="sm:group-hover:text-[color:var(--accent-11)] transition-colors ease-in-out duration-500"
        shopsiteClasses="text-lg font-semibold text-black"
      >
        {event.name}
      </Text>
      <Badge
        size="2"
        shopsiteClasses="gap-0.5 !px-1 !py-1 !rounded-lg"
        sharedClasses="w-fit max-w-full"
      >
        <span className="truncate">{event.venue.name}</span>
      </Badge>
      {event.daterange ? (
        <Badge
          size="2"
          shopsiteClasses="gap-0.5 !px-1 !py-1 !rounded-lg max-w-full"
          sharedClasses="w-fit flex sm:hidden max-w-full"
        >
          <CalendarIcon className="h-4 w-4 shrink-0" />
          <span className="max-w-[calc(100%-18px)] truncate">
            {event.daterange}
          </span>
        </Badge>
      ) : (
        <></>
      )}
      <Badge
        size="2"
        color="gray"
        shopsiteClasses="gap-0.5 !px-1 !py-1 !rounded-lg"
        sharedClasses="max-w-full w-fit"
      >
        <MapPinIcon className="h-4 w-4" />
        <span className="max-w-[calc(100%-18px)] truncate">
          {event.venue.location}
          {event.venue.region && event.venue.location !== event.venue.region
            ? `, ${event.venue.region}`
            : ""}
        </span>
      </Badge>
    </Link>
  );
}
